// Signature + ultimate (and Bastion's brace) for the VS AI pilot. Simplified, readable
// versions of the player kits, built on the same FX / damage / projectile-zone systems:
//   VOLT     Arc Dash: lunge through the player, then a chained discharge along the path.
//            Overload: crackling reactor aura; lightning arcs to the player in waves, +speed.
//   BASTION  Brace (passive): standing still raises an amber shield bubble, -30% damage.
//            Siege Field: a barrier wall toward the player that stops the player's shots.
//            Fortress Protocol: anchors and walks a telegraphed artillery barrage onto the player.
//   WRAITH   Phase Echo: phases out (intangible), blinks past the player and fires a volley.
//            Eclipse: four echo orbs orbit the pilot and snipe at the player.

import Phaser from 'phaser';
import { DEPTH, PLAYER_BODY } from '../../config/constants';
import { COLORS } from '../../config/palette';
import type { EnemyApi, GameContext } from '../../core/contracts';
import type { MechId, Tag } from '../../core/types';
import { TEX } from '../../art/keys';
import { MECHS } from '../../data/mechs';

/** What the kit needs from its pilot. */
export interface KitPilot {
  readonly x: number;
  readonly feetY: number;
  readonly onGround: boolean;
  readonly speedX: number;
  readonly isAlive: boolean;
  /** Body centre y. */
  readonly cy: number;
  /** The rival EnemyApi (excluded from the bot's own explosions) and its view root (afterimages). */
  readonly self: EnemyApi;
  readonly viewRoot: Phaser.GameObjects.Container;
  /** Forced dash toward dir for `sec` (ignores cooldown). */
  lunge(dir: 1 | -1, sec: number, speedMult: number): void;
  teleport(x: number, feetY: number): void;
  /** Fire the current weapon `n` times at the player right now. */
  volley(n: number): void;
}

interface Task {
  t: number;
  fn: () => void;
}

/** Seconds between signatures and ultimate charge time, per difficulty. */
const SIG_CD = [11, 8, 6];
const ULT_TIME = [34, 24, 17];
const DMG = [0.45, 0.7, 0.9];

export class BotKit {
  private sigT: number;
  private ultT: number;
  private tasks: Task[] = [];
  private objs: Phaser.GameObjects.GameObject[] = [];
  private removers: (() => void)[] = [];
  private stillT = 0;
  private bubble: Phaser.GameObjects.Image | null = null;
  /** Active ultimate seconds left (and its per-mech state). */
  private ultLeft = 0;
  private aura: Phaser.GameObjects.Image | null = null;
  private orbs: Phaser.GameObjects.Image[] = [];
  private age = 0;
  /** Bastion anchored by Fortress (the pilot must not move). */
  anchored = false;
  /** Wraith phase: intangible. */
  phased = 0;
  /** Volt Overload speed bonus. */
  speedMult = 1;
  /** Incoming damage multiplier (Bastion brace). */
  get damageTaken(): number {
    return this.braced ? 0.7 : 1;
  }
  private braced = false;
  private readonly color: number;

  constructor(
    private readonly ctx: GameContext,
    private readonly mech: MechId,
    private readonly level: number,
    private readonly pilot: KitPilot,
  ) {
    this.color = MECHS[mech]?.reactorColor ?? COLORS.volt;
    this.sigT = SIG_CD[level] * (0.5 + Math.random() * 0.5);
    this.ultT = ULT_TIME[level];
  }

  private get dmg(): number {
    return DMG[this.level] ?? 0.7;
  }

  private after(t: number, fn: () => void): void {
    this.tasks.push({ t, fn });
  }

  private keep<T extends Phaser.GameObjects.GameObject>(o: T): T {
    this.objs.push(o);
    return o;
  }

  private free(o: Phaser.GameObjects.GameObject | null): void {
    if (!o) return;
    const i = this.objs.indexOf(o);
    if (i >= 0) this.objs.splice(i, 1);
    o.destroy();
  }

  private hit(amount: number, tags: Tag[], x: number, y: number, kb = 260, label = 'rival'): void {
    this.ctx.damage.hitPlayer({ amount: amount * this.dmg, tags, source: 'enemy', sourceX: x, sourceY: y, knockback: kb, label });
  }

  update(dt: number, fighting: boolean, dist: number, los: boolean): void {
    this.age += dt;
    for (let i = this.tasks.length - 1; i >= 0; i--) {
      const k = this.tasks[i];
      k.t -= dt;
      if (k.t <= 0) {
        this.tasks.splice(i, 1);
        if (this.pilot.isAlive) k.fn();
      }
    }
    this.phased = Math.max(0, this.phased - dt);
    this.updateBrace(dt);
    this.updateUlt(dt);
    if (!fighting || !this.pilot.isAlive || !this.ctx.player.alive) return;
    this.sigT -= dt;
    this.ultT -= dt;
    if (this.ultT <= 0 && this.ultLeft <= 0 && los && dist < 900) {
      this.ultT = ULT_TIME[this.level];
      this.ultimate();
    } else if (this.sigT <= 0 && los && dist < 700) {
      this.sigT = SIG_CD[this.level] * (0.85 + Math.random() * 0.3);
      this.signature(dist);
    }
  }

  // ───────────────────────────── Signatures ─────────────────────────────

  private signature(dist: number): void {
    const p = this.ctx.player;
    const dir: 1 | -1 = p.x >= this.pilot.x ? 1 : -1;
    const { fx } = this.ctx;
    if (this.mech === 'volt') {
      // Arc Dash: lunge through the player, then discharge along the path.
      const x0 = this.pilot.x;
      const y0 = this.pilot.cy;
      fx.ring(x0, y0, 60, { color: this.color, width: 3 });
      this.pilot.lunge(dir, Math.min(0.32, dist / 1600 + 0.1), 1.4);
      this.after(0.36, () => {
        const x1 = this.pilot.x;
        const y1 = this.pilot.cy;
        fx.lightning([{ x: x0, y: y0 }, { x: (x0 + x1) / 2, y: (y0 + y1) / 2 - 20 }, { x: x1, y: y1 }], { color: this.color, width: 3, duration: 260, jitter: 16, branches: true });
        // The player was on the lunge path: chained.
        const lo = Math.min(x0, x1) - 40;
        const hi = Math.max(x0, x1) + 40;
        const pl = this.ctx.player;
        if (pl.x >= lo && pl.x <= hi && Math.abs(pl.y - (y0 + y1) / 2) < 110) {
          fx.lightning([{ x: x1, y: y1 }, { x: pl.x, y: pl.y }], { color: this.color, width: 3, duration: 200, jitter: 12 });
          this.hit(34, ['electric'], x1, y1, 320, 'arc_dash');
        }
        this.ctx.audio.play('chain_zap', { volume: 0.6 });
      });
      this.ctx.audio.play('dash', { volume: 0.7 });
    } else if (this.mech === 'bastion') {
      this.siegeField(dir);
    } else {
      // Phase Echo: intangible, blink past the player, then a volley.
      const fromX = this.pilot.x;
      fx.afterimage(this.pilot.viewRoot, { color: this.color, alpha: 0.5, duration: 600 });
      fx.ring(fromX, this.pilot.cy, 70, { color: this.color, width: 3 });
      this.phased = 0.9;
      let tx = p.x - dir * 220;
      if (tx < 80 || tx > this.ctx.world.width - 80 || this.ctx.world.isSolidAt(tx, p.feetY - 40)) tx = p.x + dir * 220;
      const ground = this.ctx.world.groundBelow(tx, p.y - 40, 400) ?? p.feetY;
      this.after(0.25, () => {
        this.pilot.teleport(tx, ground);
        fx.ring(tx, ground - PLAYER_BODY.h / 2, 70, { color: this.color, width: 3 });
        fx.afterimage(this.pilot.viewRoot, { color: this.color, alpha: 0.6, duration: 500 });
        this.ctx.audio.play('phase_echo', { volume: 0.6 });
      });
      this.after(0.45, () => this.pilot.volley(3));
    }
  }

  private siegeField(dir: 1 | -1): void {
    const scene = this.ctx.scene;
    const w = 26;
    const h = 190;
    const x = this.pilot.x + dir * 90;
    const ground = this.ctx.world.groundBelow(x, this.pilot.feetY - 30, 400) ?? this.pilot.feetY;
    const rect = { kind: 'rect' as const, x: x - w / 2, y: ground - h, w, h };
    const g = this.keep(scene.add.graphics().setDepth(DEPTH.PLAYER + 1));
    const draw = (a: number) => {
      g.clear();
      g.fillStyle(this.color, 0.18 * a).fillRect(rect.x, rect.y, w, h);
      g.lineStyle(3, this.color, 0.9 * a).strokeRect(rect.x, rect.y, w, h);
      for (let yy = rect.y + 12; yy < rect.y + h; yy += 22) g.lineStyle(1, this.color, 0.45 * a).lineBetween(rect.x + 3, yy, rect.x + w - 3, yy);
    };
    draw(1);
    const remove = this.ctx.projectiles.addZone({
      shape: rect,
      affects: 'player',
      onEnter: (p) => {
        this.ctx.fx.impact(p.x, p.y, Math.atan2(-p.vy, -p.vx), 'shield', this.color);
        p.kill();
      },
    });
    this.removers.push(remove);
    this.ctx.fx.ring(x, ground - h / 2, 90, { color: this.color, width: 4 });
    this.ctx.audio.play('siege_field', { volume: 0.6 });
    const life = 6;
    for (let i = 1; i <= 6; i++) this.after(life - 0.6 + i * 0.1, () => draw(1 - i / 6));
    this.after(life + 0.05, () => {
      remove();
      this.free(g);
    });
  }

  // ───────────────────────────── Ultimates ─────────────────────────────

  private ultimate(): void {
    const { fx } = this.ctx;
    const scene = this.ctx.scene;
    fx.ring(this.pilot.x, this.pilot.cy, 160, { color: this.color, width: 5, fromRadius: 30 });
    this.ctx.audio.play(this.mech === 'volt' ? 'overload' : this.mech === 'bastion' ? 'fortress_protocol' : 'eclipse', { volume: 0.8 });
    if (this.mech === 'volt') {
      this.ultLeft = 5;
      this.speedMult = 1.3;
      this.aura = this.keep(scene.add.image(this.pilot.x, this.pilot.cy, TEX.soft).setTint(this.color).setBlendMode(Phaser.BlendModes.ADD).setDepth(DEPTH.PLAYER - 2).setDisplaySize(190, 190));
      for (let i = 0; i < 7; i++) {
        this.after(0.4 + i * 0.65, () => {
          const p = this.ctx.player;
          if (Math.hypot(p.x - this.pilot.x, p.y - this.pilot.cy) > 620) return;
          const mid = { x: (p.x + this.pilot.x) / 2 + (Math.random() - 0.5) * 60, y: Math.min(p.y, this.pilot.cy) - 60 };
          fx.lightning([{ x: this.pilot.x, y: this.pilot.cy - 20 }, mid, { x: p.x, y: p.y }], { color: this.color, width: 3, duration: 180, jitter: 18, branches: true });
          this.hit(13, ['electric'], this.pilot.x, this.pilot.cy, 120, 'overload');
        });
      }
    } else if (this.mech === 'bastion') {
      this.ultLeft = 6;
      this.anchored = true;
      for (let i = 0; i < 9; i++) {
        this.after(0.3 + i * 0.6, () => {
          const p = this.ctx.player;
          const tx = p.x + p.vx * 0.5 + (Math.random() - 0.5) * 90;
          const ty = this.ctx.world.groundBelow(tx, p.y - 40, 600) ?? p.feetY;
          const r = 105;
          fx.warnCircle(tx, ty - 10, r, 700, { color: this.color });
          this.after(0.7, () => {
            this.ctx.damage.explode(tx, ty - 10, r, { amount: 30 * this.dmg, tags: ['explosive'], source: 'enemy', label: 'fortress' }, { hurtsPlayer: true, color: this.color, exclude: new Set([this.pilot.self]) });
          });
        });
      }
    } else {
      this.ultLeft = 5.5;
      for (let i = 0; i < 4; i++) {
        this.orbs.push(this.keep(scene.add.image(this.pilot.x, this.pilot.cy, TEX.soft).setTint(this.color).setBlendMode(Phaser.BlendModes.ADD).setDepth(DEPTH.PLAYER + 1).setDisplaySize(34, 34)));
      }
      for (let i = 0; i < 8; i++) {
        this.after(0.5 + i * 0.6, () => {
          const orb = this.orbs[i % this.orbs.length];
          if (!orb?.active) return;
          const p = this.ctx.player;
          const ang = Math.atan2(p.y - orb.y, p.x - orb.x) + (Math.random() - 0.5) * 0.12;
          this.ctx.projectiles.spawn({ owner: 'enemy', x: orb.x, y: orb.y, angle: ang, speed: 1500, damage: 16 * this.dmg, tags: ['kinetic'], texture: TEX.projRail, tint: this.color, glow: true, radius: 6, lifetime: 1.2, trail: 'rail' });
          fx.muzzleFlash(orb.x, orb.y, ang, this.color);
        });
      }
    }
  }

  private updateUlt(dt: number): void {
    if (this.ultLeft <= 0) return;
    this.ultLeft -= dt;
    if (this.aura?.active) {
      this.aura.setPosition(this.pilot.x, this.pilot.cy).setAlpha(0.35 + 0.15 * Math.sin(this.age * 18)).setDisplaySize(190 + 20 * Math.sin(this.age * 9), 190);
    }
    this.orbs.forEach((o, i) => {
      const a = this.age * 2.4 + (i * Math.PI) / 2;
      o.setPosition(this.pilot.x + Math.cos(a) * 70, this.pilot.cy - 10 + Math.sin(a) * 40).setAlpha(0.8 + 0.2 * Math.sin(this.age * 10 + i));
    });
    if (this.ultLeft <= 0) {
      this.speedMult = 1;
      this.anchored = false;
      this.free(this.aura);
      this.aura = null;
      for (const o of this.orbs) this.free(o);
      this.orbs = [];
    }
  }

  // ───────────────────────────── Bastion brace ─────────────────────────────

  private updateBrace(dt: number): void {
    if (this.mech !== 'bastion') return;
    const still = this.pilot.isAlive && this.pilot.onGround && Math.abs(this.pilot.speedX) < 25;
    this.stillT = still ? this.stillT + dt : 0;
    const on = this.stillT >= 0.6 || this.anchored;
    this.braced = on;
    if (!this.bubble) {
      const key = this.ctx.scene.textures.exists(TEX.shield) ? TEX.shield : TEX.soft;
      this.bubble = this.keep(this.ctx.scene.add.image(0, 0, key).setTint(this.color).setBlendMode(Phaser.BlendModes.ADD).setDepth(DEPTH.PLAYER + 1).setAlpha(0));
    }
    const target = on ? 0.2 + 0.05 * Math.sin(this.age * 6) : 0;
    const a = this.bubble.alpha + (target - this.bubble.alpha) * Math.min(1, dt * 10);
    // Round bubble around the whole (1.3x-scale) frame.
    const d = PLAYER_BODY.h * 1.3 * 1.35;
    this.bubble.setPosition(this.pilot.x, this.pilot.feetY - (PLAYER_BODY.h * 1.3) / 2).setDisplaySize(d, d).setAlpha(a).setVisible(a > 0.005);
  }

  destroy(): void {
    for (const r of this.removers) r();
    this.removers = [];
    for (const o of this.objs) o.destroy();
    this.objs = [];
    this.tasks = [];
  }
}
